import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getCart, GetCartApiParameter, updateCart } from "@insite/client-framework/Services/CartService";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState, isCartEmpty } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel, ProductInventoryDto, WarehouseDto, WishListLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getQuoteState } from "@MasterBluePrint/Store/Data/Quotes/QuotesSelector";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import addToCartCollectionTracking from "@MasterBluePrint/Tracking/AddToCart/addToCartCollectionTracking";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { useDispatch } from "react-redux";
import { getProductRealTimeInventory } from "@insite/client-framework/Services/ProductServiceV2";

const mapDispatchToProps = {
    addLinesToCart,
    clearCurrentCart,
    getCurrentCartState,
};

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    return {
        quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
        isCartEmpty: isCartEmpty(cart),
        currentState: state,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        state,
    };
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface RfqQuoteDetailSubtotalStyles {
    container?: GridContainerProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValueGridItem?: GridItemProps;
    subtotalValue?: TypographyProps;
    discountsLabelGridItem?: GridItemProps;
    discountsLabel?: TypographyProps;
    discountsValueGridItem?: GridItemProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabelGridItem?: GridItemProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingLabelNew?: TypographyProps;
    shippingAndHandlingValueGridItem?: GridItemProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabelGridItem?: GridItemProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValueGridItem?: GridItemProps;
    otherChargesValue?: TypographyProps;
    taxLabelGridItem?: GridItemProps;
    taxLabel?: TypographyProps;
    taxValueGridItem?: GridItemProps;
    taxValue?: TypographyProps;
    totalLabelGridItem?: GridItemProps;
    totalLabel?: TypographyProps;
    totalValueGridItem?: GridItemProps;
    totalValue?: TypographyProps;
    sideWrapper?: InjectableCss;
    quoteBtnGridItem?: GridItemProps;
    QuoteToCart?: ButtonPresentationProps;
    confirmRemoveAllModal?: ModalPresentationProps;
    confirmContainer?: InjectableCss;
    confirmMessage?: InjectableCss;
    confirmButtons?: InjectableCss;
    cancelButton?: InjectableCss;
    closeContainer?: InjectableCss;
    removeButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

export const rfqQuoteDetailSubtotalStyles: RfqQuoteDetailSubtotalStyles = {
    container: {
        gap: 10,
        css: css`
            background: ${getColor("common.background")};
            padding: 1.25rem 0;
            .border-seperator {
                border-bottom: 1px solid ${themeColors.colorLightPureBlue};
                padding-top: 0.9375rem;
                margin: 0 0.3125rem 0.9375rem 0.3125rem;
                width: 100%;
                display: block;
            }
            > div {
                div:nth-child(odd) {
                    > span {
                        color: ${themeColors.colorCharcoalGrey};
                        font-family: Roboto, sans-serif;
                        font-size: ${themeFontSizes.size16};
                        font-weight: ${themeFontWeights.fontWeightMedium};
                        letter-spacing: -0.31px;
                        line-height: ${themeIndentSizes.indent23};
                        &.total-value {
                            color: ${themeColors.colorRed};
                            font-family: Roboto;
                            font-size: ${themeFontSizes.size22};
                            font-weight: ${themeFontWeights.fontWeightForBold};
                            letter-spacing: -0.42px;
                            line-height: ${themeIndentSizes.indent23};
                            text-align: right;
                        }
                    }
                    > span.subtotal-value {
                        font-weight: bold;
                        color: ${themeColors.colorCharcoalGrey};
                    }
                    .total-label {
                        color: ${themeColors.colorCharcoalGrey};
                        font-family: Roboto, sans-serif;
                        font-size: ${themeFontSizes.size20};
                        font-weight: ${themeFontWeights.fontWeightMedium};
                        letter-spacing: -0.38px;
                        line-height: ${themeIndentSizes.indent23};
                        text-align: left !important;
                    }
                }
                div:nth-child(even) {
                    text-align: right;
                    > span {
                        color: ${themeColors.colorCharcoalGrey};
                        font-family: Roboto;
                        font-size: ${themeFontSizes.size20};
                        letter-spacing: -0.38px;
                        line-height: ${themeIndentSizes.indent23};
                    }
                    > span.subtotal-value {
                        font-weight: ${themeFontWeights.fontWeightForBold};
                    }
                }
            }
        `,
    },
    subtotalLabelGridItem: {
        width: 6,
    },
    subtotalValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsLabelGridItem: { width: 6 },
    discountsValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingLabelGridItem: { width: 6 },
    shippingAndHandlingValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    sideWrapper: {
        css: css`
            span.quote-items {
                border-bottom: 1px solid #c2d1d9;
                color: ${themeColors.colorCharcoalGrey};
                font-family: "Lato", sans-serif;
                font-size: 0.9375rem;
                font-weight: ${themeFontWeights.fontWeightForBold};
                letter-spacing: 0.17px;
                line-height: 1.125rem;
                padding-bottom: 1rem;
                display: block;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                margin-bottom: 0.9375rem;
                            `,
                        null,
                        null,
                    ],
                    "max",
                )}
            }
        `,
    },
    shippingAndHandlingLabelNew: {
        css: css`
            position: relative;
            font-size: 1rem;
            font-weight: ${themeFontWeights.fontWeightMedium};
            -webkit-letter-spacing: -0.31px;
            -moz-letter-spacing: -0.31px;
            -ms-letter-spacing: -0.31px;
            letter-spacing: -0.31px;
            line-height: 1.4375rem;
            width: 100%;
            text-align: right;
            justify-content: right;
            display: flex;
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesLabelGridItem: { width: 6 },
    otherChargesValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxLabelGridItem: { width: 6 },
    taxValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabelGridItem: { width: 6 },
    totalLabel: { weight: "bold" },
    totalValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
    quoteBtnGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 12,
    },
    QuoteToCart: {
        css: css`
            text-transform: uppercase;
            height: 44px;
            font-size: 0.875rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: 1;
                            width: 100%;
                            margin: 2rem 0 0.9375rem 0;
                        `,
                        css`
                            width: 100%;
                            margin: 2rem 0 0.9375rem 0;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                            max-width: 100%;
                            z-index: 1;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    confirmButtons: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            padding-top: 1.625rem;
            margin-top: 1.875rem;
            border-top: 1px solid #d7d7d7;
        `,
    },
    confirmMessage: {
        css: css`
            h1 {
                color: #002554;
                font-family: "Roboto", sans-serif;
                font-size: 1.25rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 32px;
                position: relative;
                margin-bottom: 2.1875rem;
                margin-top: 0.5rem;

                &:after {
                    height: 2px;
                    width: 44px;
                    background-color: #c63527;
                    content: "";
                    display: inline-block;
                    position: absolute;
                    bottom: -12px;
                    left: 1px;
                }
            }

            .icon-alert {
                svg {
                    transform: scale(1.5);
                    stroke: #c63527;
                }
            }

            p {
                color: #002554;
                font-family: Lato, sans-serif;
                font-size: 0.9375rem;
                letter-spacing: 0;
                line-height: 1.3125rem;
            }
        `,
    },
    removeButton: {
        buttonType: "solid",
        css: css`
            border-radius: 2px;
            background-color: #002554;
            border: 2px solid #002554;
            color: #fff;
            text-transform: uppercase;

            &:hover,
            &:active {
                background-color: #002554;
                border: 2px solid #002554;
            }
            span {
                color: #fff;
            }
        `,
    },
    cancelButton: {
        css: css`
            color: #002554;
            font-family: Roboto;
            font-size: 0.9375rem;
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-align: center;
            text-decoration: underline;
            cursor: pointer;
            margin-left: 3em;
            margin-right: 3em;
            justify-content: center;
            width: auto;
        `,
    },
    confirmRemoveAllModal: {
        sizeVariant: "small",
    },
    closeContainer: {
        css: css`
            position: absolute;
            right: 1.25rem;
            top: 24px;
            .close-label {
                color: #535e64;
                font-family: Roboto;
                font-size: 0.875rem;
                letter-spacing: 0.16px;
                line-height: 1;
                display: inline-block;
                margin-right: 0.625rem;
                vertical-align: middle;
            }
            .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        `,
    },
    centeringWrapper: {
        css: css`
            background: rgba(0, 37, 84, 0.2);
            height: 100%;
            width: 100%;
            position: fixed;
            z-index: 99;
            left: 0;
            top: 0;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
            color: #002554;
        `,
    },
};

const styles = rfqQuoteDetailSubtotalStyles;

const RfqQuoteDetailSubtotal = ({
    quoteState,
    isCartEmpty,
    toaster,
    clearCurrentCart,
    showAddToCartConfirmationDialog,
    addLinesToCart,
    state
}: Props) => {
    const quote = quoteState.value;
    const [isConfirmRemoveAll, setIsConfirmRemoveAll] = React.useState(false);
    const [showLoader, setShowLoader] = React.useState(false);
    const [cartStatus, setCartStatus] = React.useState(false);
    const dispatch = useDispatch();

    if (!quote) {
        return null;
    }

    const confirmRemoveAllClose = () => {
        setIsConfirmRemoveAll(false);
    };
    const confirmRemoveAll = () => {
        setShowLoader(true);
        clearCurrentCart();
        setIsConfirmRemoveAll(false);
        setShowLoader(false);
    };

    const AddQuoteToCart = () => {
        if (!isCartEmpty) {
            setIsConfirmRemoveAll(true);
        } else {
            setShowLoader(true);
            addToCartClickHandler();
        }
    };
    const addToCartClickHandler = async () => {
        if (!quote || !quote.cartLines) {
            return;
        }
        const cartLines: CartLineModel[] = [];
        const productInventoryDto: ProductInventoryDto[] = [];
        for (const cartLine of quote.cartLines) {
            const cartLineClone = cloneDeep(cartLine);
            cartLineClone.properties = {
                InQuote: "true",
            };
            try {
                const result = await getProductRealTimeInventory({
                    productId: cartLine.productId ?? "",
                    configuration: [],
                    expand: ["warehouses"],
                    additionalQueryStringParameters: {},
                });
                if (result) {
                    const inventoryDto: ProductInventoryDto = {
                        additionalResults: null,
                        inventoryAvailabilityDtos: null,
                        inventoryWarehousesDtos: result.inventoryWarehousesDtos ?? [],
                        productId: cartLine.productId ?? "",
                        qtyOnHand: 0,
                    };
                    productInventoryDto.push(inventoryDto);
                } else {
                    console.error("No inventory data returned for productId", cartLine.productId);
                }
            } catch (error) {
                console.error("Error fetching inventory", error);
            }
            cartLines.push(cartLineClone);
        }
        addLinesToCart({
            apiParameter: {
                cartId: API_URL_CURRENT_FRAGMENT,
                cartLineCollection: { cartLines } as CartLineCollectionModel,
            },
            onSuccess: async () => {
                if (showAddToCartConfirmationDialog) {
                    toaster.addToast({ body: siteMessage("Cart_AllProductsAddedToCart"), messageType: "success" });
                }
                const cartData = await getCart({ cartId: API_URL_CURRENT_FRAGMENT, expand: ["shipping", "validation", "cartLines"] } as GetCartApiParameter);
                const { cart } = cartData;
                if (cart.lineCount > 0) {
                    const cartClone = cloneDeep(cart) as Draft<typeof cart>;
                    cartClone.properties = {
                        isquote: JSON.stringify(true),
                        quoteNumber: quote.orderNumber,
                        termsCode: quote.properties.termsCode,
                        customerFreightBillingAcctNumber: quote.properties.customerFreightBillingAcctNumber,
                    };
                    // cartClone.type = "Quote";
                    await updateCart({ cart: cartClone });
                    cartClone.properties = { ...cart.properties, ...cartClone.properties };
                    dispatch({
                        type: "Data/Carts/CompleteLoadCart",
                        apiParameter: { cartId: "current", expand: ["shipping", "validation", "cartLines"] },
                        model: cartClone,
                        isCurrent: true,
                        replaceCart: true,
                    });
                    if (cartClone.cartLines && cartClone.cartLines.length > 0) {
                        addToCartCollectionTracking(
                            { cartLines: cartClone.cartLines } as CartLineCollectionModel,
                            state,
                            productInventoryDto,
                        );
                    }
                }
                setCartStatus(true);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });
    };
    React.useEffect(() => {
        if (cartStatus && !isCartEmpty) {
            setShowLoader(false);
        }
    }, [addToCartClickHandler]);

    return (
        <>
            {showLoader && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            <Typography as="div" {...styles.sideWrapper} className="side-wrapper">
                {quote?.quoteLineCollection?.length === 1 ? (
                    <Typography className="quote-items">
                        {" "}
                        {`${translate("There is")} ${quote?.quoteLineCollection?.length} ${translate(
                            "item in this quote",
                        )}`}
                    </Typography>
                ) : (
                    <Typography className="quote-items">
                        {" "}
                        {`${translate("There are")} ${quote?.quoteLineCollection?.length} ${translate(
                            "items in this quote",
                        )}`}
                    </Typography>
                )}
                <GridContainer {...styles.container}>
                    <GridItem {...styles.subtotalLabelGridItem}>
                        <Typography {...styles.subtotalLabel}>{translate("Subtotal")}</Typography>
                    </GridItem>
                    <GridItem {...styles.subtotalValueGridItem}>
                        <Typography {...styles.subtotalValue} className="subtotal-value">
                            {quote.orderSubTotalDisplay}
                        </Typography>
                    </GridItem>
                    {quote.orderSubTotalWithOutProductDiscounts > 0 && (
                        <>
                            <GridItem {...styles.discountsLabelGridItem}>
                                <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                            </GridItem>
                            <GridItem {...styles.discountsValueGridItem}>
                                <Typography
                                    {...styles.discountsValue}
                                >{`-${quote.orderSubTotalWithOutProductDiscountsDisplay}`}</Typography>
                            </GridItem>
                        </>
                    )}
                    {quote.showTaxAndShipping && quote.shippingAndHandlingDisplay.length > 0 && (
                        <>
                            <GridItem {...styles.shippingAndHandlingLabelGridItem}>
                                <Typography {...styles.shippingAndHandlingLabel}>
                                    {translate("Shipping & Handling")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.shippingAndHandlingValueGridItem}>
                                <Typography {...styles.shippingAndHandlingValue}>
                                    {quote.shippingAndHandling === 0 ? (
                                        <Typography {...styles.shippingAndHandlingLabelNew}>
                                            {translate("ShippingandHandlingYetToBeCalculated")}
                                        </Typography>
                                    ) : (
                                        quote.shippingAndHandlingDisplay
                                    )}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                    {(!quote.customerOrderTaxes || quote.customerOrderTaxes.length === 0) && (
                        <>
                            <GridItem {...styles.taxLabelGridItem}>
                                <Typography {...styles.taxLabel}>{translate("Tax")}</Typography>
                            </GridItem>
                            <GridItem {...styles.taxValueGridItem}>
                                <Typography {...styles.taxValue} data-test-selector="orderDetails_totalTaxDisplay">
                                    {quote.totalTaxDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    )}

                    <Typography as="span" className="border-seperator"></Typography>
                    <GridItem {...styles.totalLabelGridItem}>
                        <Typography {...styles.totalLabel} className="total-label">
                            {translate("Total")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.totalValueGridItem}>
                        <Typography
                            {...styles.totalValue}
                            className="total-value"
                            data-test-selector="orderDetail_orderGrandTotalDisplay"
                        >
                            {quote.orderGrandTotalDisplay}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.quoteBtnGridItem}>
                        <Button {...styles.QuoteToCart} onClick={AddQuoteToCart} data-test-selector="quote-to-cart-btn">
                            {translate("Add this Quote to Cart")}
                        </Button>
                        <Modal
                            isOpen={isConfirmRemoveAll}
                            data-test-selector="confirmRemoveAllModal"
                            {...styles.confirmRemoveAllModal}
                            handleClose={() => confirmRemoveAllClose()}
                            className="confirm-remove-modal"
                        >
                            <Typography as="div" {...styles.confirmContainer}>
                                <Typography as="div" onClick={confirmRemoveAllClose} {...styles.closeContainer}>
                                    <span className="close-label">Close</span>
                                    <Icon className="icon-close" src="X" />
                                </Typography>
                                <Typography as="div" {...styles.confirmMessage}>
                                    <Icon className="icon-alert" src="AlertCircle" />
                                    <h1>{translate("Remove all products?")}</h1>
                                    <p>
                                        {translate(
                                            "Before you can proceed with this quote, please remove the existing items from cart.",
                                        )}
                                    </p>
                                </Typography>
                                <Typography as="div" {...styles.confirmButtons}>
                                    <Clickable onClick={confirmRemoveAllClose} {...styles.cancelButton}>
                                        {translate("Cancel")}
                                    </Clickable>
                                    <Button onClick={confirmRemoveAll} {...styles.removeButton}>
                                        {translate("Remove All Products")}
                                    </Button>
                                </Typography>
                            </Typography>
                        </Modal>
                    </GridItem>
                </GridContainer>
            </Typography>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(RfqQuoteDetailSubtotal)),
    definition: {
        displayName: "Quote Detail Total",
        fieldDefinitions: [],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
